// eslint-disable-next-line max-classes-per-file
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';

import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import _ from 'lodash';
import { Activity } from 'app/model/pipeline/activity';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import {
    InvokeADFPipelineActivity,
    AzureBlobStorageActivity,
    AzureDevOpsActivity,
    EmailActivity,
    ExtensibleActivity,
    TeamsActivity
} from 'app/model/pipeline/extensible.activity';
import { Credential, LogicAppsProperty, ReferenceAdapter } from 'shell-models-lib';
import { IActivityContext, IOffice365OutlookActivityContext, ITeamsActivityContext } from 'app/interfaces/copilot.pipeline.action.types';

export abstract class ExtensibleActivityAdapter<T, U, D extends IActivityContext<string> = IActivityContext<string>> extends ActivityAsyncAdapter<ExtensibleActivity, Model.ExtensibleActivity, D> {

    public model(): IModel {
        return Metamodel.modelFor(Activity);
    }

    protected async _toResource(activity: ExtensibleActivity): Promise<Model.ExtensibleActivity> {
        const modelActivity: Model.ExtensibleActivity = super._getCommonActivityProperties(activity, Metamodel.modelOf(activity).name) as Model.ExtensibleActivity;
        modelActivity.typeProperties = {
            inputs: {
                method: activity.method,
                path: activity.filledPath(),
                body: activity.properties.reduce((json, property) => this._processProperty(property, json), {})
            },
            connection: activity.connection,
            credential: activity.credential && !__IS_FABRIC_DPE__ ? ReferenceAdapter.toResource(activity.credential, this.context) as Model.CredentialReference
                : undefined
        };

        if (activity instanceof InvokeADFPipelineActivity) {
            modelActivity.typeProperties.inputs.queries = { 'x-ms-api-version': '2017-09-01-preview' };
        }

        if (activity instanceof AzureDevOpsActivity) {
            const property = activity.properties.find((p) => p.name === 'account');
            modelActivity.typeProperties.inputs.queries = { account: property.value };
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            // Overlay this on spark activity
            _.merge(modelActivity, parsed);
        }
        return modelActivity;
    }

    protected async _toModel(modelActivity: Model.ExtensibleActivity): Promise<ExtensibleActivity> {
        const metaModel = Metamodel.modelByName(modelActivity.type);
        const activity = metaModel.create<ExtensibleActivity>(modelActivity.name);
        this._updatedCommonProperties(modelActivity, activity);

        if (modelActivity.typeProperties) {
            activity.method = modelActivity.typeProperties.inputs?.method;
            activity.path = modelActivity.typeProperties.inputs?.path;
            if (modelActivity.typeProperties.inputs?.body) {
                const flatBody = this._flattenJson(modelActivity.typeProperties.inputs.body);
                Object.keys(flatBody).forEach((propertyName) => {
                    const p = activity.addProperty();
                    p.name = propertyName;
                    p.value = flatBody[propertyName];
                    p.in = 'body';
                    p.type = 'placeholder';
                    p.format = 'placeholder';
                    p.description = 'placeholder';
                    p.label = propertyName;
                    p.required = false;
                    p.children = [];
                });
            }
            if (modelActivity.typeProperties.inputs?.queries) {
                activity.account = modelActivity.typeProperties.inputs?.queries['account'];
            }
            activity.connection = modelActivity.typeProperties.connection;
            activity.credential = ReferenceAdapter.toProxyModel<Credential>(Metamodel.modelFor(Credential), modelActivity.typeProperties.credential, this.context);
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, metaModel));
        return activity;
    }

    protected _updateProperty(properties: LogicAppsProperty[], propertyName: string, value: string): void {
        for (const property of properties) {
            if (property.name === propertyName) {
                ValueAdapter.toModel(property, LogicAppsProperty.VALUE, value);
                return;
            }
            if (property.children && property.children.length > 0) {
                this._updateProperty(property.children, propertyName, value);
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _setNestedPropertyValue(json: any, name: string, value: string): any {
        const parts = name.split('.');
        let currentObj = json;
        for (let i = 0; i < parts.length - 1; i++) {
            const part = parts[i];
            if (!currentObj.hasOwnProperty(part)) {
                currentObj[part] = {};
            }
            currentObj = currentObj[part];
        }
        currentObj[parts[parts.length - 1]] = value;
        return json;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _processProperty(property: LogicAppsProperty, json: any) {
        if (property.in === 'body') {
            this._setNestedPropertyValue(json, property.name, LogicAppsProperty.VALUE.templateValue(property) ? LogicAppsProperty.VALUE.templateValue(property) : property.value);
        }
        if (property.children) {
            property.children.forEach((childProp) => {
                this._processProperty(childProp, json);
            });
        }
        return json;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _flattenJson(jsonObj: any) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return Object.entries(jsonObj).reduce((acc: any, [key, val]) => {
            if (typeof val === 'object' && val !== null) {
                Object.assign(acc, this._flattenJson(val));
                Object.keys(acc).forEach((subKey) => {
                    acc[`${key}.${subKey}`] = acc[subKey];
                    delete acc[subKey];
                });
            } else {
                acc[key] = val;
            }
            return acc;
        }, {});
    }
}

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(EmailActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        dependsOn: '*',
        policy: '*',
        userProperties: '*',
        typeProperties: {
            inputs: '*',
            credential: '*'
        }
    }
})
export class EmailActivityAdapter extends ExtensibleActivityAdapter<EmailActivity, Model.ExtensibleActivity, IOffice365OutlookActivityContext> {
    public override async toFabricCopilotContext(activity: EmailActivity): Promise<IOffice365OutlookActivityContext> {
        if (activity) {
            const activityContext: IOffice365OutlookActivityContext = await super.toFabricCopilotContext(activity);
            const toProperty = activity.properties?.find((p) => p.name === 'To');
            const subjectProperty = activity.properties?.find((p) => p.name === 'Subject');
            const bodyProperty = activity.properties?.find((p) => p.name === 'Body');
            const importanceProperty = activity.properties?.find((p) => p.name === 'Importance');
            const fromProperty = activity.properties?.find((p) => p.name === 'From');
            const ccProperty = activity.properties?.find((p) => p.name === 'Cc');
            const bccProperty = activity.properties?.find((p) => p.name === 'Bcc');
            const replyToProperty = activity.properties?.find((p) => p.name === 'ReplyTo');

            if (toProperty) {
                activityContext.ToEmailAddress = ValueAdapter.toResource(toProperty, LogicAppsProperty.VALUE, true);
            }
            if (subjectProperty) {
                activityContext.Subject = ValueAdapter.toResource(subjectProperty, LogicAppsProperty.VALUE, true);
            }
            if (bodyProperty) {
                activityContext.Body = ValueAdapter.toResource(bodyProperty, LogicAppsProperty.VALUE, true);
            }
            if (importanceProperty) {
                activityContext.Importance = ValueAdapter.toResource(importanceProperty, LogicAppsProperty.VALUE, true) as 'Low' | 'Normal' | 'High';
            }
            if (fromProperty) {
                activityContext.FromEmailAddress = ValueAdapter.toResource(fromProperty, LogicAppsProperty.VALUE, true);
            }
            if (ccProperty) {
                activityContext.CarbonCopyEmailAddress = ValueAdapter.toResource(ccProperty, LogicAppsProperty.VALUE, true);
            }
            if (bccProperty) {
                activityContext.BlindCarbonCopyEmailAddress = ValueAdapter.toResource(bccProperty, LogicAppsProperty.VALUE, true);
            }
            if (replyToProperty) {
                activityContext.ReplyToEmailAddress = ValueAdapter.toResource(replyToProperty, LogicAppsProperty.VALUE, true);
            }

            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IOffice365OutlookActivityContext,
        model?: EmailActivity
    ): Promise<EmailActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.ToEmailAddress) {
                this._updateProperty(model.properties, 'To', context.ToEmailAddress);
            }
            if (context.Subject) {
                this._updateProperty(model.properties, 'Subject', context.Subject);
            }
            if (context.Body) {
                this._updateProperty(model.properties, 'Body', context.Body);
            }
            if (context.Importance) {
                this._updateProperty(model.properties, 'Importance', context.Importance);
            }
            if (context.FromEmailAddress) {
                this._updateProperty(model.properties, 'From', context.FromEmailAddress);
            }
            if (context.CarbonCopyEmailAddress) {
                this._updateProperty(model.properties, 'Cc', context.CarbonCopyEmailAddress);
            }
            if (context.BlindCarbonCopyEmailAddress) {
                this._updateProperty(model.properties, 'Bcc', context.BlindCarbonCopyEmailAddress);
            }
            if (context.ReplyToEmailAddress) {
                this._updateProperty(model.properties, 'ReplyTo', context.ReplyToEmailAddress);
            }
        }
        return model;
    }
}

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(TeamsActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        dependsOn: '*',
        policy: '*',
        userProperties: '*',
        typeProperties: {
            inputs: '*',
            credential: '*'
        }
    }
})
export class TeamsActivityAdapter extends ExtensibleActivityAdapter<TeamsActivity, Model.ExtensibleActivity, ITeamsActivityContext> {
    public override async toFabricCopilotContext(activity: TeamsActivity): Promise<ITeamsActivityContext> {
        if (activity) {
            const activityContext: ITeamsActivityContext = await super.toFabricCopilotContext(activity);
            const locationProperty = activity.properties?.find((p) => p.name === 'location');
            const bodyProperty = activity.properties?.find((p) => p.name === 'body');
            const channelId = activity.properties?.find((p) => p.name === 'recipient.channelId');
            const groupId = activity.properties?.find((p) => p.name === 'recipient.groupId');
            const recipient = activity.properties?.find((p) => p.name === 'recipient');
            const message = activity.properties?.find((p) => p.name === 'messageBody');
            if (locationProperty) {
                activityContext.MessageType = ValueAdapter.toResource(locationProperty, LogicAppsProperty.VALUE, true) === 'Channel' ? 'Channel' : 'GroupChat';
            }
            if (recipient) {
                activityContext.MessageType = 'GroupChat';
                activityContext.GroupChatId = ValueAdapter.toResource(recipient, LogicAppsProperty.VALUE, true);
            }
            if (channelId) {
                activityContext.MessageType = 'Channel';
                activityContext.ChannelId =  ValueAdapter.toResource(channelId, LogicAppsProperty.VALUE, true);
            }
            if (groupId) {
                activityContext.MessageType = 'Channel';
                activityContext.TeamId =  ValueAdapter.toResource(groupId, LogicAppsProperty.VALUE, true);
            }
            if (message) {
                activityContext.MessageBody = ValueAdapter.toResource(message, LogicAppsProperty.VALUE, true);
            }
            if (bodyProperty) {
                const groupIdProperty = bodyProperty.children?.find((p) => p.name === 'recipient.groupId');
                const channelIdProperty = bodyProperty.children?.find((p) => p.name === 'recipient.channelId');
                const recipientProperty = bodyProperty.children?.find((p) => p.name === 'recipient');
                const messageProperty = bodyProperty.children?.find((p) => p.name === 'messageBody');
                if (messageProperty) {
                    activityContext.MessageBody = ValueAdapter.toResource(messageProperty, LogicAppsProperty.VALUE, true);
                }
                if (groupIdProperty) {
                    activityContext.TeamId = ValueAdapter.toResource(groupIdProperty, LogicAppsProperty.VALUE, true);
                }
                if (channelIdProperty) {
                    activityContext.ChannelId = ValueAdapter.toResource(channelIdProperty, LogicAppsProperty.VALUE, true);
                }
                if (recipientProperty) {
                    activityContext.GroupChatId = ValueAdapter.toResource(recipientProperty, LogicAppsProperty.VALUE, true);
                }
            }
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: ITeamsActivityContext,
        model?: TeamsActivity
    ): Promise<TeamsActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.MessageType) {
                this._updateProperty(model.properties, 'location', context.MessageType);
            }
            if (context.TeamId) {
                this._updateProperty(model.properties, 'recipient.groupId', context.TeamId);
            }
            if (context.ChannelId) {
                this._updateProperty(model.properties, 'recipient.channelId', context.ChannelId);
            }
            if (context.GroupChatId) {
                this._updateProperty(model.properties, 'recipient', context.GroupChatId);
            }
        }
        return model;
    }
}

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureBlobStorageActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        dependsOn: '*',
        policy: '*',
        userProperties: '*',
        typeProperties: {
            inputs: '*',
            credential: '*'
        }
    }
})
export class AzureBlobStorageActivityAdapter extends ExtensibleActivityAdapter<AzureBlobStorageActivity, Model.ExtensibleActivity> {
}

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureDevOpsActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        dependsOn: '*',
        policy: '*',
        userProperties: '*',
        typeProperties: {
            inputs: '*',
            credential: '*'
        }
    }
})
export class AzureDevOpsActivityAdapter extends ExtensibleActivityAdapter<AzureDevOpsActivity, Model.ExtensibleActivity> {
}

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(InvokeADFPipelineActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        dependsOn: '*',
        policy: '*',
        userProperties: '*',
        typeProperties: {
            inputs: '*',
            credential: '*'
        }
    }
})
export class InvokeADFPipelineActivityAdapter extends ExtensibleActivityAdapter<InvokeADFPipelineActivity, Model.ExtensibleActivity> {
}
